import { Flex, SimpleGrid, Text } from "@chakra-ui/react";
import { Section, type SectionProps } from "components/section";
import { useWarehouseCdp } from "contexts/warehouse-cdp-context";
import React from "react";
import Balancer from "react-wrap-balancer";
import { Chip } from "ui/chips";
import { Superscript, TextTitle } from "ui/text";

type WarehouseAsCdpProps = {
  title?: string;
  description?: string;
} & SectionProps;

export function WarehouseAsCdp({ title, description, ...sectionProps }: WarehouseAsCdpProps) {
  const { warehouse } = useWarehouseCdp();

  const cdpFeatures = [
    {
      title: "Fast time to value",
      description: `Don’t implement a separate technology — bring your CDP to your data and turn ${warehouse.name} into a marketing engine. Implement your first use case in weeks, not months.`,
    },
    {
      title: "Flexibility & security",
      description: `Your data is unique, and you need a CDP that allows you to leverage all of your behavioral data, attributes, offline data, and custom data science models. All while keeping your data where it is most secure, in ${warehouse.name}.`,
    },
    {
      title: "Complete customer view",
      description: `The most complete view of your customer already lives in ${warehouse.name}, so keep it there. Your CDP should act as an extension of your data infrastructure, not a separate entity that sits outside of your technology stack.`,
    },
  ];
  return (
    <Section {...sectionProps}>
      <TextTitle
        align="center"
        size="lg"
        descriptionMaxW="600px"
        title={<Balancer>{title ?? `Why ${warehouse.name} should be your CDP`}</Balancer>}
        description={
          description ?? (
            <>
              90%
              <Superscript
                size="sm"
                href="https://digiday.com/marketing/the-vast-majority-of-marketers-are-unhappy-with-their-customer-data-platforms-but-then-again-not-many-are-sure-what-they-really-are/"
              />{" "}
              of marketers say their traditional CDP does not do what they need, so why do they keep buying them?
            </>
          )
        }
      />

      <SimpleGrid
        columns={{ base: 1, md: 3 }}
        gap={{ base: "bigger", md: "big", lg: "biggest" }}
        mt={{ base: "biggest", lg: "huge" }}
      >
        {cdpFeatures.map((feature) => (
          <Flex key={feature.title} align="start" direction="column" gap="small">
            <Chip as="h3" variant={warehouse.color}>
              {feature.title}
            </Chip>
            <Text color="brand.textSecondary.light" textStyle="caption1">
              {feature.description}
            </Text>
          </Flex>
        ))}
      </SimpleGrid>
    </Section>
  );
}
